import { UserClaimsService } from './../../../../services/authentication/user-claims.service';
import { autoinject, transient, BindingEngine, observable, useView } from "aurelia-framework";
import { DataType, QueryFilter, DataGridColumn, SourceType } from "../../../../models/queries/queries.models";
import { I18N } from "aurelia-i18n";
import { CustomGridPageBase } from "../../common/custom-grid-page.base";
import KeyDownEvent = JQuery.KeyDownEvent;
import { Guid } from "../../../../utilities/guid";
import { Claims } from '../../../../constants/authentication/claims';
import { QueriesClientService } from '../../../../services/queries/queries-client.services';
import { computedFrom } from "aurelia-binding";
import * as moment from "moment";

@autoinject()
@transient()
@useView('views/pages/modules/customers/customers-list.html')
export class CustomersList extends CustomGridPageBase {

    @observable()
    filter: string;
    appliedFilter: string;
    public defaultPageCount: number = 10;
    public getItemRoute = (id) => `/customer/${id}`;
    public initialFilters: QueryFilter[];

    @observable
    public searchText: string = "";

    @observable 
    public isVettingCustomer: boolean;

    // Number of vetting customers
    public vettingCustomerCount: number;

    public canAddCustomers: boolean = false;

    constructor(i18N: I18N, protected bindingEngine: BindingEngine, private userClaimsService: UserClaimsService,
        private queryService: QueriesClientService) {
        super(i18N, "Customer", bindingEngine);
    }

    activate(){
        this.setDefaultFilters();
        this.refreshActivityCounts();
    }
    
    searchTextChanged(){
        this.appliedFilter = this.filter;
        this.setItemSource();
    }

    protected setDefaultFilters() {
        this.appliedFilter = this.filter;
        this.setItemSource();
    }

    private getItemSource(filters) {
        this.columns = this.getColumns();
        return {
            sourceType: SourceType.ODataUrl,
            source: "odata/Customers",
            columns: this.columns,
            initialFilters: filters,
            initialSorting: this.initialSorting
        };
    }
    
    private setItemSource() {
        const currentFilters: QueryFilter[] = [];

        if (this.searchText) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("name", this.searchText, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("code", this.searchText, "contains({0}, '{1}')", DataType.Default, false, true, guid, true, true));
        }

        if(this.isVettingCustomer) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("isVettingCustomer", this.isVettingCustomer, "eq", DataType.Boolean, true, true, guid, false, false))
        }

        this.itemSource = this.getItemSource(currentFilters);
    }

    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;

        if (e.key == "Enter") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }

    filterChanged() {
        if ((this.filter && this.filter.length >= 5) || this.filter == "") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }

    clearFilter() {
        this.filter = "";
        this.searchText = "";
        this.appliedFilter = this.filter;
        this.setItemSource();
    }
    
    getColumns(): DataGridColumn[] {
        return [
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                name: 'name',
                title: 'Name',
                instancePerRow: true,
                value: '<a class="link" href="customer/${item.id}">${item.name}</a>',
                isSortable: true,
                parameter: {
                    item: null
                }
            },
            {
                columnOrder: 2,
                sortOrder: 2,
                dataType: DataType.Default,
                name: 'code',
                title: 'Code',
                isSortable: true,
            },
            {
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.Date,
                name: 'createdDate',
                title: 'Created Date',
                isSortable: true,
                valueConverter: (date: Date | string) => date
                ? moment(date).format('DD MMM YYYY HH:mm')
                : ''
                
            },
            {
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.Default,
                name: 'customerType',
                title: 'Type',
                isSortable: true                
            }];
    }

    /**
     * 'isVettingFilter' active status
     */
    @computedFrom('isVettingCustomer')
    public get vettingFilterActive() {
        return this.isVettingCustomer;
    }

    /**
     * Toggle vetting customer filter
     */
    public toggleVettingCustomerFilter() {
        this.isVettingCustomer = !this.isVettingCustomer
        this.setItemSource();
    }

    /**
     * Refresh filter activity counts
     */
    refreshActivityCounts() {
        if (this.userClaimsService.hasPermission(Claims.CanAccessCustomers)) {
            this.queryService.getData(`odata/Customers/GetStatusCounts`).then(statusCounts => {
                
                this.clearCounts();

                // Loop over status counts
                for (let i = 0; i < statusCounts.keys.length; i++) {
                    const statusKey = statusCounts.keys[i];
                    const statusCount = statusCounts.values[i];

                    // -1 is reserved for the 'vettingCustomerCount' status count
                    if (statusKey == -1) {
                        this.vettingCustomerCount = statusCount;
                    }
                }
            });
        }
    }

    /**
     * Clear status counts
     */
    clearCounts() {
        this.vettingCustomerCount = 0;
    }
}